/**
 * list_approvals tool - List pending approvals
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const listApprovalsInputSchema = z.object({
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  top: z
    .number()
    .optional()
    .default(25)
    .describe("Maximum number of approvals to return (default 25)."),
});

export type ListApprovalsInput = z.infer<typeof listApprovalsInputSchema>;

export const listApprovalsTool = {
  name: "list_approvals",
  description:
    "List pending approvals in the environment. Shows approval requests that are waiting for a response.",
  inputSchema: {
    type: "object" as const,
    properties: {
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      top: {
        type: "number",
        description: "Maximum number of approvals to return (default 25).",
      },
    },
  },
};

export async function handleListApprovals(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = listApprovalsInputSchema.parse(input);

  logger.info({ environment: parsed.environment }, "Executing list_approvals");

  try {
    const approvals = await api.listApprovals(parsed.environment, parsed.top);

    if (approvals.length === 0) {
      return { text: "No pending approvals found.", structured: { count: 0, approvals: [] } };
    }

    const lines = [`Pending Approvals (${approvals.length}):`];
    for (const approval of approvals) {
      lines.push(`  • ${approval.title}`);
      lines.push(`    ID: ${approval.id}`);
      lines.push(`    Status: ${approval.status}`);
      lines.push(`    Requested by: ${approval.requestedBy}`);
      lines.push(`    Created: ${new Date(approval.createdTime).toLocaleString()}`);
    }

    // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
    const structured: Record<string, unknown> = {
      count: approvals.length,
      approvals: approvals.map((approval) => ({
        id: approval.id,
        title: approval.title,
        status: approval.status,
        requestedBy: approval.requestedBy,
        createdTime: approval.createdTime,
      })),
    };

    return { text: lines.join("\n"), structured };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    // The approvals API may require additional permissions or Dataverse access
    if (msg.includes("Authentication") || msg.includes("401") || msg.includes("403")) {
      return {
        text: "Approvals API requires additional permissions. Approvals are stored in Dataverse and may require the Dataverse API scope. Check your app registration has the required permissions.",
      };
    }
    throw error;
  }
}
